import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { StyledProps } from 'styled-components'
import { useDebounce } from '../../../hooks'
import { IconSearch } from '../../iconography/Icons/Icons'

export type SearchInputProps = {
  placeholder?: string
  value?: string
  className?: string
  onChange: (value: string, event?: React.ChangeEvent) => void
  inputRef?: React.Ref<HTMLInputElement>
  autoFocus?: boolean
  debounceValue?: number
  'data-test-id'?: string
}

const Wrapper = styled.div`
  height: 20px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`

type ThemedProps = StyledProps<{}>
const InputStyled = styled.input<ThemedProps>`
  border: none;
  outline: none;
  background: none;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  order: 2;
  color: ${({ theme }: ThemedProps) => theme.color.onPrimaryDark};
  flex-grow: 1;

  &::placeholder {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }: ThemedProps) => theme.color.secondary200};
  }

  ${props => props.theme.media.mobile`
      font-size: 16px;
      &::placeholder {
        font-size: 16px;
      }
  `}
`
const IconStyled = styled(IconSearch)<ThemedProps>`
  margin-right: 11px;
  order: 1;
  transition: 0.3s fill;

  ${InputStyled}:focus + & {
    fill: ${({ theme }: ThemedProps) => theme.color.primary600};
  }
`

export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = '',
  onChange,
  value,
  className,
  inputRef,
  autoFocus,
  debounceValue = 500,
  'data-test-id': dataTestId,
}: SearchInputProps) => {
  const [search, setSearch] = useState(value)
  const initial = useRef(!value)
  const debouncedSearch = useDebounce(search, debounceValue)
  const onSearchChange = useCallback(event => {
    setSearch(event.target.value)
  }, [])

  useEffect(() => {
    if (!initial.current) {
      onChange(debouncedSearch || undefined)
    } else {
      initial.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  return (
    <Wrapper className={className}>
      <InputStyled
        placeholder={placeholder}
        value={search || ''}
        onChange={onSearchChange}
        ref={inputRef}
        autoFocus={autoFocus}
        data-test-id={dataTestId}
      />
      <IconStyled />
    </Wrapper>
  )
}
